import type { ReportPick, ReportPickSliceContext } from "./reportPickListStorage";
import type { TradePolicyCatalogField, TradePolicyPolicyNode, TradePolicyRuleNode } from "../types/tradePolicy";
import { TRADE_POLICY_BUCKET_METRIC_DEFINITIONS } from "../constants/tradePolicyBucketMetricDefinitions";
import {
  parseCandleDirectionTradeDirectionKey,
  parseRegimeDirectionKey,
  parseRegimeDirectionTradeDirectionKey,
  parseSessionCandleDirectionKey,
  parseSessionMarketConditionCandleTradeDirectionKey,
  parseSessionMarketConditionKey,
  parseSessionMarketConditionTradeDirectionKey,
  parseSessionTradeDirectionKey,
} from "./conditionAnalysisSelectors";
import { formatDirectionBarsWindow } from "./marketRegimeDirectionLookbacks";

/** Filter catalog maps used when converting report picks → policy_tree nodes. */
export type PickImportCatalogContext = {
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  /** `marginal_bucket_import.eq_string_prefixes` from GET /v1/trade-policy/filter-catalog. */
  marginalEqPrefixes?: Record<string, string>;
};

/** Defaults (unknown TF → 120/480). Pick import labels rarely have report candle TF. */
const DIRECTION_LONG_BARS_LABEL = formatDirectionBarsWindow("long");
const DIRECTION_SHORT_BARS_LABEL = formatDirectionBarsWindow("short");

function summarySliceIdPart(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

const TRADE_TYPE_PATH = "trade.trade_type";

const REGIME_PATH_CANDIDATES = [
  "regime.market_condition",
  "structure.candle_regime",
  "structure.market_regime",
  "context.candle_regime",
  "entry.candle_regime",
  "entry.context.candle_regime",
];

const CANDLE_DIR_LONG_PATH_CANDIDATES = [
  "regime.direction",
  "structure.candle_direction",
  "context.candle_direction",
  "entry.candle_direction",
];

const CANDLE_DIR_SHORT_PATH_CANDIDATES = [
  "regime.direction_short",
  "structure.candle_direction_short",
  "structure.direction_short",
  "context.direction_short",
  "entry.candle_direction_short",
  "entry.direction_short",
];

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

export function isPolicyNode(v: unknown): v is TradePolicyPolicyNode {
  if (!isPlainRecord(v)) return false;
  const k = v.kind;
  if (k === "rule") {
    return typeof v.path === "string" && typeof v.op === "string" && "value" in v;
  }
  if (k === "group") {
    return (v.op === "and" || v.op === "or") && Array.isArray(v.children);
  }
  return false;
}

function matchEnumToken(field: TradePolicyCatalogField, token: string): string | null {
  const tl = token.trim().toLowerCase();
  const ev = field.enum_values ?? [];
  // Keep FE aligned with backend normalization for legacy aliases.
  // Example: consistency bucket tokens may appear as mid/med but catalog uses "medium".
  if ((tl === "mid" || tl === "med") && field.path.toLowerCase().includes("consistency")) {
    for (const e of ev) {
      if (e.toLowerCase() === "medium") return e;
    }
  }
  for (const e of ev) {
    if (e.toLowerCase() === tl) return e;
  }
  for (const e of ev) {
    const en = e.toLowerCase().replace(/\s+/g, "_");
    if (tl === en) return e;
  }
  return null;
}

function enumEqRule(path: string, field: TradePolicyCatalogField, token: string): TradePolicyRuleNode | null {
  if (field.value_kind !== "enum") return null;
  const v = matchEnumToken(field, token);
  if (v == null) return null;
  const ops = field.allowed_ops ?? [];
  if (ops.includes("eq")) return { kind: "rule", path, op: "eq", value: v };
  if (ops.includes("in")) return { kind: "rule", path, op: "in", value: [v] };
  return null;
}

function validatePolicyNodesAgainstCatalog(
  raw: unknown[],
  fieldsByPath: Map<string, TradePolicyCatalogField>
): { ok: true; nodes: TradePolicyPolicyNode[] } | { ok: false; unresolved: string[] } {
  const unresolved: string[] = [];

  const validateNode = (n: unknown): TradePolicyPolicyNode | null => {
    if (!isPolicyNode(n)) return null;
    if (n.kind === "group") {
      const children = (n.children ?? []).map(validateNode).filter(Boolean) as TradePolicyPolicyNode[];
      if (children.length === 0) return null;
      return { kind: "group", op: n.op, children };
    }
    const field = fieldsByPath.get(n.path);
    if (!field) {
      unresolved.push(n.path);
      return null;
    }
    const ops = field.allowed_ops ?? [];
    if (!ops.includes(n.op as any)) {
      unresolved.push(`${n.path}:${n.op}`);
      return null;
    }
    if (field.value_kind === "enum") {
      if (n.op === "in") {
        if (!Array.isArray(n.value) || n.value.length === 0) {
          unresolved.push(`${n.path}:value`);
          return null;
        }
        const mapped: string[] = [];
        for (const v of n.value) {
          const m = matchEnumToken(field, String(v));
          if (!m) {
            unresolved.push(`${n.path}:${String(v)}`);
            return null;
          }
          mapped.push(m);
        }
        return { kind: "rule", path: n.path, op: "in", value: mapped };
      }
      const m = matchEnumToken(field, String(n.value));
      if (!m) {
        unresolved.push(`${n.path}:${String(n.value)}`);
        return null;
      }
      return { kind: "rule", path: n.path, op: n.op, value: m };
    }
    if (field.value_kind === "number") {
      const num = typeof n.value === "number" ? n.value : Number(n.value);
      if (!Number.isFinite(num)) {
        unresolved.push(`${n.path}:number`);
        return null;
      }
      return { kind: "rule", path: n.path, op: n.op, value: num };
    }
    // Unknown value kinds: accept as-is (backend-provided canonical), but require field existence/op support.
    return n;
  };

  const nodes = raw.map(validateNode).filter(Boolean) as TradePolicyPolicyNode[];
  if (unresolved.length) return { ok: false, unresolved };
  if (nodes.length === 0) return { ok: false, unresolved: ["policyNodes"] };
  return { ok: true, nodes };
}

/** Bucket keys from static metric definitions (aligned with report situation strings). */
function bucketKeysFromMetricDefinitions(clauseKey: string): string[] | null {
  const k = clauseKey.trim();
  const root = TRADE_POLICY_BUCKET_METRIC_DEFINITIONS as Record<string, unknown>;
  const raw = root[k];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const buckets = (raw as Record<string, unknown>).buckets;
  if (!buckets || typeof buckets !== "object" || Array.isArray(buckets)) return null;
  return Object.keys(buckets as Record<string, unknown>);
}

/**
 * When explicit paths miss, find a catalog enum field whose allowed values overlap situation buckets
 * for this metric (handles filter-catalog paths that differ from FE guesses).
 */
function findEnumFieldByBucketOverlap(
  clauseKey: string,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): TradePolicyCatalogField | null {
  const defKeys = bucketKeysFromMetricDefinitions(clauseKey);
  if (!defKeys?.length) return null;
  const tokenSet = new Set(defKeys.map((t) => t.toLowerCase().replace(/\s+/g, "_")));
  const k = clauseKey.trim().toLowerCase();
  let best: TradePolicyCatalogField | null = null;
  let bestScore = -1;
  for (const f of Array.from(fieldsByPath.values())) {
    if (f.value_kind !== "enum") continue;
    const ev = f.enum_values ?? [];
    if (ev.length === 0) continue;
    let overlap = 0;
    for (const e of ev) {
      const en = e.toLowerCase().replace(/\s+/g, "_");
      if (tokenSet.has(en)) overlap += 1;
    }
    if (overlap === 0) continue;
    const pl = f.path.toLowerCase();

    // Avoid false positives for ambiguous bucket vocab like low/medium/high.
    // Example: `consistency:*` is a blended context score bucket and should NOT map to `atr_regime`
    // just because both share enum tokens like "medium".
    if (k === "consistency" && !pl.includes("consistency")) continue;

    let score = overlap * 6;
    if (pl.includes(k)) score += 40;
    if (pl.endsWith(`.${k}`) || pl.endsWith(`_${k}`)) score += 25;
    if (k === "sr_room" && pl.includes("strength")) score -= 80;
    if (k === "sr_strength" && pl.includes("sr_room") && !pl.includes("strength")) score -= 40;
    const trustworthy = pl.includes(k) || overlap >= 2 || (k === "sr_room" && overlap >= 3);
    if (!trustworthy) continue;
    if (score > bestScore) {
      bestScore = score;
      best = f;
    }
  }
  return best;
}

const SR_ROOM_BUCKET_TOKENS = ["good", "wide", "tight", "very_tight"] as const;

/**
 * Catalog paths for SR “room” often omit the substring `sr_room`; match by the four bucket tokens instead.
 */
function findSrRoomEnumField(fieldsByPath: Map<string, TradePolicyCatalogField>): TradePolicyCatalogField | null {
  let best: TradePolicyCatalogField | null = null;
  let bestScore = -1;
  for (const f of Array.from(fieldsByPath.values())) {
    if (f.value_kind !== "enum") continue;
    const ev = new Set((f.enum_values ?? []).map((e) => e.toLowerCase().replace(/\s+/g, "_")));
    let hits = 0;
    for (const t of SR_ROOM_BUCKET_TOKENS) if (ev.has(t)) hits += 1;
    if (hits < 3) continue;
    const pl = f.path.toLowerCase();
    let score = hits * 6;
    if (pl.includes("strength") && !pl.includes("room")) score -= 40;
    if (pl.includes("bollinger") || pl.includes("bb_zone") || pl.includes("squeeze")) score -= 40;
    if (pl.includes("sr_room") || pl.includes("room_to_level")) score += 35;
    if (pl.includes("distance") || pl.includes("room_atr") || (pl.includes("room") && pl.includes("atr"))) score += 20;
    if (pl.includes("sr") && pl.includes("position")) score += 12;
    if (score > bestScore) {
      bestScore = score;
      best = f;
    }
  }
  return best;
}

function resolveMarketCondition(
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  mc: string
): TradePolicyRuleNode | null {
  for (const p of REGIME_PATH_CANDIDATES) {
    const f = fieldsByPath.get(p);
    if (!f) continue;
    const node = enumEqRule(p, f, mc);
    if (node) return node;
  }
  for (const f of Array.from(fieldsByPath.values())) {
    if (f.value_kind !== "enum") continue;
    const pl = f.path.toLowerCase();
    if (!/(candle_regime|market_regime)/.test(pl)) continue;
    const node = enumEqRule(f.path, f, mc);
    if (node) return node;
  }
  return null;
}

function resolveCandleDirection(
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  dir: string,
  window: "long" | "short"
): TradePolicyRuleNode | null {
  const paths = window === "short" ? CANDLE_DIR_SHORT_PATH_CANDIDATES : CANDLE_DIR_LONG_PATH_CANDIDATES;
  for (const p of paths) {
    const f = fieldsByPath.get(p);
    if (!f) continue;
    const node = enumEqRule(p, f, dir);
    if (node) return node;
  }
  if (window === "short") {
    for (const f of Array.from(fieldsByPath.values())) {
      if (f.value_kind !== "enum") continue;
      const pl = f.path.toLowerCase();
      if (!/(direction_short|candle_direction_short)/.test(pl)) continue;
      const node = enumEqRule(f.path, f, dir);
      if (node) return node;
    }
  } else {
    for (const f of Array.from(fieldsByPath.values())) {
      if (f.value_kind !== "enum") continue;
      const pl = f.path.toLowerCase();
      if (/short/.test(pl) && /(direction|candle)/.test(pl)) continue;
      if (!/(candle_direction|bar_direction)/.test(pl)) continue;
      const node = enumEqRule(f.path, f, dir);
      if (node) return node;
    }
  }
  return null;
}

function resolveTradeDirection(
  field: TradePolicyCatalogField | undefined,
  td: "long" | "short"
): TradePolicyRuleNode | null {
  if (!field || field.value_kind !== "enum") return null;
  const ops = field.allowed_ops ?? [];
  if (!ops.includes("eq")) return null;
  const set = new Set(field.enum_values ?? []);
  let token: string | null = null;
  if (set.has("buy") && set.has("sell")) token = td === "long" ? "buy" : "sell";
  else if (set.has("long") && set.has("short")) token = td === "long" ? "long" : "short";
  if (token == null) {
    const v = matchEnumToken(field, td);
    token = v;
  }
  if (token == null) return null;
  const canon = matchEnumToken(field, token);
  if (canon == null) return null;
  return { kind: "rule", path: field.path, op: "eq", value: canon };
}

function inferTripleCandleWindow(ctx: ReportPickSliceContext): "long" | "short" {
  if (ctx.triple_candle_window === "long" || ctx.triple_candle_window === "short") {
    return ctx.triple_candle_window;
  }
  if (ctx.kind === "session_regime_direction_short_trade_direction") return "short";
  if (ctx.kind === "session_regime_direction_trade_direction") return "long";
  if (ctx.sliceId.startsWith("regime_dir_short_td_")) return "short";
  if (ctx.sliceId.startsWith("sess_mcdstd_")) return "short";
  if (ctx.sliceId.startsWith("sess_mcd_td_")) return "long";
  return "long";
}

/**
 * Resolves slice dimensions to catalog rule nodes. When the loaded catalog does not expose a matching field/token,
 * that dimension is listed in `unresolved` so imports can fail closed (avoid sending a global bucket rule).
 */
export function sliceContextToPolicyNodes(
  ctx: ReportPickSliceContext,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): { nodes: TradePolicyPolicyNode[]; unresolved: string[] } {
  const unresolved: string[] = [];
  const nodes: TradePolicyPolicyNode[] = [];
  const tradeField = fieldsByPath.get(TRADE_TYPE_PATH);

  const pushSessionCatalogEq = () => {
    const sessionTok = ctx.session_id?.trim();
    if (!sessionTok) {
      unresolved.push("session_id");
      return;
    }
    const SESSION_PATHS = [
      "session_at_entry.session_id",
      "session_time.session_id",
      "market_context.session_time.session_id",
    ];
    let resolved = false;
    for (const pth of SESSION_PATHS) {
      const f = fieldsByPath.get(pth);
      if (!f) continue;
      const node = enumEqRule(pth, f, sessionTok);
      if (node) {
        nodes.push(node);
        resolved = true;
        break;
      }
    }
    if (!resolved) unresolved.push("session_at_entry.session_id");
  };

  const pushRegime = (mc: string | undefined, label: string) => {
    if (!mc) return;
    const r = resolveMarketCondition(fieldsByPath, mc);
    if (r) nodes.push(r);
    else unresolved.push(label);
  };

  const pushCandle = (dir: string | undefined, window: "long" | "short", label: string) => {
    if (!dir) return;
    const r = resolveCandleDirection(fieldsByPath, dir, window);
    if (r) nodes.push(r);
    else unresolved.push(label);
  };

  const pushTrade = (td: "long" | "short" | undefined, label: string) => {
    if (!td) return;
    const r = resolveTradeDirection(tradeField, td);
    if (r) nodes.push(r);
    else unresolved.push(label);
  };

  switch (ctx.kind) {
    case "all":
      break;
    case "direction":
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    case "regime":
      pushRegime(ctx.market_condition, "market_condition");
      break;
    case "market_direction":
      pushCandle(ctx.candle_direction, "long", "candle_direction_long");
      break;
    case "market_direction_short":
      pushCandle(ctx.candle_direction, "short", "candle_direction_short");
      break;
    case "candle_direction":
      pushCandle(ctx.candle_direction, "long", "candle_direction_long");
      break;
    case "candle_direction_short":
      pushCandle(ctx.candle_direction, "short", "candle_direction_short");
      break;
    case "regime_direction":
      pushRegime(ctx.market_condition, "market_condition");
      pushCandle(ctx.candle_direction, "long", "candle_direction_long");
      break;
    case "regime_direction_trade_direction": {
      const win = inferTripleCandleWindow(ctx);
      pushRegime(ctx.market_condition, "market_condition");
      pushCandle(ctx.candle_direction, win, win === "short" ? "candle_direction_short" : "candle_direction_long");
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    }
    case "candle_direction_trade_direction":
      pushCandle(ctx.candle_direction, "long", "candle_direction_long");
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    case "candle_direction_short_trade_direction":
      pushCandle(ctx.candle_direction, "short", "candle_direction_short");
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    case "session_time":
      pushSessionCatalogEq();
      break;
    case "session_trade_direction":
      pushSessionCatalogEq();
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    case "session_candle_direction":
      pushSessionCatalogEq();
      pushCandle(ctx.candle_direction, "long", "candle_direction_long");
      break;
    case "session_candle_direction_short":
      pushSessionCatalogEq();
      pushCandle(ctx.candle_direction, "short", "candle_direction_short");
      break;
    case "session_regime":
      pushSessionCatalogEq();
      pushRegime(ctx.market_condition, "market_condition");
      break;
    case "session_regime_trade_direction":
      pushSessionCatalogEq();
      pushRegime(ctx.market_condition, "market_condition");
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    case "session_regime_direction_trade_direction": {
      const win = inferTripleCandleWindow(ctx);
      pushSessionCatalogEq();
      pushRegime(ctx.market_condition, "market_condition");
      pushCandle(ctx.candle_direction, win, win === "short" ? "candle_direction_short" : "candle_direction_long");
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    }
    case "session_regime_direction_short_trade_direction":
      pushSessionCatalogEq();
      pushRegime(ctx.market_condition, "market_condition");
      pushCandle(ctx.candle_direction, "short", "candle_direction_short");
      pushTrade(ctx.trade_direction, "trade_direction");
      break;
    default:
      break;
  }

  return { nodes, unresolved };
}

export function pickContentPolicyNodes(p: ReportPick): TradePolicyPolicyNode[] {
  if (Array.isArray(p.policyNodes)) {
    const nodes = p.policyNodes.filter(isPolicyNode);
    if (nodes.length) return nodes;
  }
  if (p.rule) {
    const path = p.rule.path;
    const op = p.rule.op;
    if (!path || !op) return [];
    let value: string | number | string[] | null = null;
    if (p.rule.valueNumber != null && Number.isFinite(p.rule.valueNumber)) value = p.rule.valueNumber;
    else if (Array.isArray(p.rule.valueEnumSet) && p.rule.valueEnumSet.length) value = p.rule.valueEnumSet;
    else if (typeof p.rule.valueEnum === "string" && p.rule.valueEnum.trim()) value = p.rule.valueEnum.trim();
    if (value == null) return [];
    return [{ kind: "rule", path, op, value } as TradePolicyPolicyNode];
  }
  const meta = p.meta as Record<string, unknown> | undefined;
  // Bucket picks: metric_key + "metric:value" bucket string → resolved in pickContentPolicyNodesWithCatalog.
  if (p.kind === "bucket") {
    const mk = meta && typeof meta.metric_key === "string" ? meta.metric_key.trim() : "";
    const b = meta && typeof meta.bucket === "string" ? meta.bucket.trim() : "";
    const lbl = typeof p.label === "string" ? p.label.trim() : "";
    const blob = b || lbl;
    const colon = blob.indexOf(":");
    if ((mk && blob) || (colon > 0 && colon < blob.length - 1)) {
      return [{ kind: "group", op: "and", children: [] }];
    }
  }
  // Situation: clauses array and/or pipe-separated situation / label (same shape as report API).
  if (p.kind === "situation") {
    const clausesRaw = meta?.clauses;
    if (Array.isArray(clausesRaw) && clausesRaw.length) {
      return [{ kind: "group", op: "and", children: [] }];
    }
    const situationStr =
      (meta && typeof meta.situation === "string" && meta.situation.includes("=") && meta.situation.trim()) ||
      (typeof p.label === "string" && p.label.includes("=") ? p.label.trim() : "");
    if (situationStr) return [{ kind: "group", op: "and", children: [] }];
  }
  return [];
}

/**
 * Condition-analysis situation clause keys → filter-catalog paths.
 * Align with BE: filter_catalog — `bb_zone` → five-band `volatility.bollinger_zone`; marginal `bollinger:*` →
 * `volatility.bollinger.position` (quartiles). sr_strength → sr_positioning.strength; adx → structure.adx_bucket.
 */
function normalizeSituationClauseKey(key: string): string {
  const k = key.trim().toLowerCase().replace(/\s+/g, "_");
  // Marginal condition-analysis uses `bollinger:<quartile>` (e.g. lower_quartile). Do not fold into `bb_zone`
  // (five-band width-normalized zone); that maps to a different catalog field.
  if (k === "bb" || k === "bbands" || k === "bollinger_bands") return "bollinger";
  return k;
}

/** Parse `adx=a|sr_room=tight` from report situation strings when `clauses` is missing. */
function parsePipeEqClauses(situation: string): Array<{ key: string; value: string }> {
  const s = situation.trim();
  if (!s || (!s.includes("=") && !s.includes(":"))) return [];
  const out: Array<{ key: string; value: string }> = [];
  for (const part of s.split("|")) {
    const p = part.trim();
    const ieq = p.indexOf("=");
    const icol = p.indexOf(":");
    let splitAt = -1;
    if (ieq > 0) splitAt = ieq;
    else if (icol > 0) splitAt = icol;
    else continue;
    const k = normalizeSituationClauseKey(p.slice(0, splitAt));
    const v = p.slice(splitAt + 1).trim();
    if (k && v) out.push({ key: k, value: v });
  }
  return out;
}

function clauseScalarToString(v: unknown): string | undefined {
  if (typeof v === "string") {
    const t = v.trim();
    return t || undefined;
  }
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  return undefined;
}

function pickClauseRecordFromRaw(rec: unknown): { key: string; value: string } | null {
  if (!isPlainRecord(rec)) return null;
  const keyRaw =
    clauseScalarToString(rec.key) ??
    clauseScalarToString(rec.metric) ??
    clauseScalarToString(rec.dimension) ??
    clauseScalarToString(rec.name) ??
    clauseScalarToString(rec.field);
  if (!keyRaw) return null;
  const value =
    clauseScalarToString(rec.value) ??
    clauseScalarToString(rec.bucket) ??
    clauseScalarToString(rec.val) ??
    clauseScalarToString(rec.v);
  if (!value) return null;
  return { key: normalizeSituationClauseKey(keyRaw), value };
}

function clauseKeyToPathCandidates(key: string): string[] {
  const k = normalizeSituationClauseKey(key);
  switch (k) {
    case "adx":
      return ["structure.adx_bucket", "context.adx_bucket", "entry.adx_bucket"];
    case "trend_state":
      // Situation de-dupe token (e.g. weak_or_ranging) → ADX bucket (see BE market_context_service).
      return ["structure.adx_bucket", "context.adx_bucket", "entry.adx_bucket"];
    case "rsi":
      return ["structure.rsi_bucket", "context.rsi_bucket", "entry.rsi_bucket"];
    case "atr_regime":
      return ["volatility.atr_regime", "structure.atr_regime", "context.atr_regime", "entry.atr_regime"];
    case "candle_regime":
      return [
        "regime.market_condition",
        "structure.candle_regime",
        "structure.market_regime",
        "context.candle_regime",
        "entry.candle_regime",
        "entry.context.candle_regime",
      ];
    case "dir":
      // Condition-analysis situation clauses use `dir` for candle direction.
      return [
        "regime.direction",
        "structure.candle_direction",
        "context.candle_direction",
        "entry.candle_direction",
        "regime.direction_short",
        "structure.candle_direction_short",
        "structure.direction_short",
        "context.direction_short",
        "entry.candle_direction_short",
        "entry.direction_short",
      ];
    case "ma50":
      return [
        "trend_location.price_vs_ma.sma_50",
        "trend.ma50",
        "trend.ma_50",
        "structure.ma50",
        "structure.ma_50",
        "context.ma50",
        "entry.ma50",
      ];
    case "ma200":
      // Commonly exposed as ma200/ma_200/moving_average_200 across catalogs.
      return [
        "trend_location.price_vs_ma.sma_200",
        "trend.ma200",
        "trend.ma_200",
        "trend.moving_average_200",
        "structure.ma200",
        "structure.ma_200",
        "structure.moving_average_200",
        "context.ma200",
        "context.ma_200",
        "entry.ma200",
        "entry.ma_200",
      ];
    case "consistency":
      return [
        "context_consistency.consistency_bucket",
        "structure.consistency",
        "context.consistency",
        "entry.consistency",
        "regime.consistency",
      ];
    case "htf_weighted":
      return [
        "htf_alignment.weighted_alignment_score",
        "structure.htf_weighted",
        "context.htf_weighted",
        "entry.htf_weighted",
        "trend.htf_weighted",
      ];
    case "sr_room":
      return [
        "sr_positioning.room",
        "sr_positioning.sr_room",
        "sr_positioning.room_to_level",
        "structure.sr_room",
        "structure.room_to_resistance_atr",
        "structure.room_to_support_atr",
        "structure.distance_to_level_atr",
        "context.sr_room",
        "entry.sr_room",
        "sr.sr_room",
        "sr.room",
        "structure.room_to_level",
      ];
    case "bb_zone":
      return [
        "volatility.bollinger_zone",
        "structure.bb_zone",
        "volatility.bb_zone",
        "context.bb_zone",
        "entry.bb_zone",
        "structure.bollinger_zone",
      ];
    case "bollinger":
      // Marginal Bollinger (quartiles, etc.) → volatility.bollinger.position (filter_catalog after deploy).
      return [
        "volatility.bollinger.position",
        "volatility.bollinger_position",
        "structure.bollinger.position",
        "structure.bollinger_position",
        "context.bollinger.position",
        "context.bollinger_position",
        "entry.bollinger.position",
        "entry.bollinger_position",
      ];
    case "squeeze":
      return [
        "volatility.volatility_squeeze",
        "volatility.squeeze",
        "structure.squeeze",
        "context.squeeze",
        "entry.squeeze",
      ];
    case "sr_strength":
      return [
        "sr_positioning.strength",
        "structure.sr_strength",
        "context.sr_strength",
        "entry.sr_strength",
        "sr.strength",
        "sr.zone_strength",
        "structure.zone_strength",
        "sr_positioning.zone_strength",
      ];
    case "session":
      return [
        "session_at_entry.session_id",
        "session_time.session_id",
        "market_context.session_time.session_id",
      ];
    default:
      return [];
  }
}

/** When explicit paths miss, find an enum catalog field whose path matches the situation clause key. */
function resolveClauseByPathScan(
  clauseKey: string,
  value: string,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): TradePolicyRuleNode | null {
  const k = clauseKey.trim().toLowerCase();
  const val = value.trim();
  if (!k || !val) return null;

  const pathMatchesClause = (path: string): boolean => {
    const pl = path.toLowerCase();
    if (k === "ma200") {
      if (pl.endsWith(".ma200") || pl.includes(".ma200.") || pl.includes("_ma200")) return true;
      if (pl.endsWith(".ma_200") || pl.includes(".ma_200.") || pl.includes("_ma_200")) return true;
      if (pl.includes("moving_average_200") || pl.includes("movingaverage_200")) return true;
      if (pl.includes("sma_200") || pl.includes("sma200")) return true;
      if (pl.includes("ma") && pl.includes("200") && (pl.includes("trend") || pl.includes("structure") || pl.includes("context")))
        return true;
      return false;
    }
    if (k === "ma50") {
      if (pl.includes("sma_50") || pl.includes("sma50")) return true;
      if (pl.endsWith(".ma50") || pl.includes(".ma50.") || pl.includes("_ma50")) return true;
      if (pl.endsWith(".ma_50") || pl.includes(".ma_50.")) return true;
      return false;
    }
    if (k === "trend_state" || k === "adx") {
      return pl.includes("adx_bucket");
    }
    if (k === "candle_regime") {
      if (pl.includes("candle_regime")) return true;
      if (pl.includes("market_regime")) return true;
      // Some catalogs name this "market_condition" for the same dimension.
      if (pl.includes("market_condition") && (pl.includes("regime") || pl.includes("structure") || pl.includes("context")))
        return true;
      return false;
    }
    if (k === "dir") {
      // Clause key is abbreviated; match candle direction fields.
      if (pl.endsWith(".dir") || pl.includes(".dir.") || pl.endsWith("_dir")) return true;
      if (pl.includes("candle_direction")) return true;
      if (pl.includes("direction_short") || pl.includes("candle_direction_short")) return true;
      if (pl.endsWith(".direction") || pl.includes(".direction.")) return true;
      return false;
    }
    if (k === "bb_zone") {
      if (pl === "volatility.bollinger_zone") return true;
      if (pl.includes("bollinger_zone")) return true;
      if (pl.includes("bb_zone")) return true;
      // Five-band zone only — not quartile position.
      if (pl.includes("bollinger") && pl.includes("zone") && !pl.includes("position")) return true;
      return false;
    }
    if (k === "bollinger") {
      if (pl === "volatility.bollinger.position" || pl.endsWith(".bollinger.position")) return true;
      if (pl.includes("bollinger") && pl.includes("position")) return true;
      if (pl.includes("bollinger_position")) return true;
      return false;
    }
    if (k === "sr_strength") {
      if (pl === "sr_positioning.strength") return true;
      if (pl.includes("sr_strength")) return true;
      if (pl.includes("zone_strength") && (pl.includes("sr") || pl.includes("support") || pl.includes("resistance")))
        return true;
      if (pl.includes("sr_positioning") && pl.includes("strength")) return true;
      return false;
    }
    if (k === "sr_room") {
      if (pl.includes("sr_room")) return true;
      if (pl.includes("room_to_level")) return true;
      if (pl.includes("room_atr")) return true;
      if (pl.includes("distance_to") && (pl.includes("level") || pl.includes("sr") || pl.includes("resistance") || pl.includes("support")))
        return true;
      if (pl.includes("sr") && pl.includes("room") && !pl.includes("strength")) return true;
      return false;
    }
    if (k === "consistency") {
      if (pl.endsWith(".consistency") || pl.includes(".consistency.")) return true;
      return false;
    }
    if (k === "htf_weighted") {
      if (pl.includes("htf_weighted")) return true;
      return false;
    }
    return pl.endsWith(`.${k}`) || pl.includes(`.${k}.`) || pl.endsWith(`_${k}`);
  };

  for (const f of Array.from(fieldsByPath.values())) {
    if (f.value_kind !== "enum") continue;
    if (!pathMatchesClause(f.path)) continue;
    const node = enumEqRule(f.path, f, val);
    if (node) return node;
  }
  return null;
}

/**
 * Situation strings sometimes use bucket shorthand that differs from filter-catalog enum tokens
 * (e.g. consistency `mid` vs `medium`).
 */
function situationClauseValueCandidates(key: string, raw: string): string[] {
  const v = raw.trim();
  if (!v) return [];
  const lower = v.toLowerCase();
  const out = new Set<string>([v, lower]);
  const k = key.trim().toLowerCase();
  if (k === "consistency") {
    // Backend + simulator normalize mid/med → medium. Keep FE aligned (never prefer "mid").
    if (lower === "mid" || lower === "med") {
      out.add("medium");
      out.add("Medium");
    }
  }
  if (k === "htf_weighted") {
    if (lower === "mid") {
      out.add("medium");
    }
    if (lower === "medium") {
      out.add("mid");
    }
  }
  if (k === "trend_state") {
    // BE collapses low-trend-strength situations into trend_state=weak_or_ranging → ADX=weak.
    if (lower === "weak_or_ranging" || lower === "weak" || lower === "ranging") {
      out.add("weak");
      out.add("Weak");
    }
  }
  if (k === "bollinger") {
    // Five-band shorthand from UI/docs → try quartile tokens used by marginal condition analysis + catalog.
    if (lower === "lower" || lower === "lower_band") {
      out.add("lower_quartile");
    }
    if (lower === "upper" || lower === "upper_band") {
      out.add("upper_quartile");
    }
  }
  return Array.from(out);
}

function resolveOneSituationClause(key: string, val: string, catalog: PickImportCatalogContext): TradePolicyRuleNode | null {
  const { fieldsByPath, marginalEqPrefixes } = catalog;
  const nk = normalizeSituationClauseKey(key);
  const paths = clauseKeyToPathCandidates(nk);
  const valueCandidates = situationClauseValueCandidates(nk, val);
  let resolved: TradePolicyRuleNode | null = null;
  for (const valTry of valueCandidates) {
    for (const pth of paths) {
      const f = fieldsByPath.get(pth);
      if (!f) continue;
      const node = enumEqRule(pth, f, valTry);
      if (node) {
        resolved = node;
        break;
      }
    }
    if (resolved) break;
  }
  if (!resolved && marginalEqPrefixes) {
    const path = marginalEqPrefixes[nk];
    if (path) {
      const f = fieldsByPath.get(path);
      if (f) {
        for (const valTry of valueCandidates) {
          const node = enumEqRule(path, f, valTry);
          if (node) {
            resolved = node;
            break;
          }
        }
      }
    }
  }
  if (!resolved) {
    for (const valTry of valueCandidates) {
      const scanned = resolveClauseByPathScan(nk, valTry, fieldsByPath);
      if (scanned) {
        resolved = scanned;
        break;
      }
    }
  }
  return resolved;
}

function gatherClausesForPick(p: ReportPick): Array<{ key: string; value: string }> {
  const meta = p.meta as Record<string, unknown> | undefined;
  if (p.kind === "bucket") {
    let metricKey = meta && typeof meta.metric_key === "string" ? meta.metric_key.trim() : "";
    let bucketStr = meta && typeof meta.bucket === "string" ? meta.bucket.trim() : "";
    if (!bucketStr && typeof p.label === "string") bucketStr = p.label.trim();
    if (!bucketStr) return [];
    if (!metricKey && bucketStr.includes(":")) {
      const idx = bucketStr.indexOf(":");
      metricKey = bucketStr.slice(0, idx).trim();
      const valueOnly = bucketStr.slice(idx + 1).trim();
      if (metricKey && valueOnly) return [{ key: normalizeSituationClauseKey(metricKey), value: valueOnly }];
      return [];
    }
    if (!metricKey) return [];
    const prefix = `${metricKey}:`;
    let value = "";
    if (
      bucketStr.length >= prefix.length &&
      bucketStr.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase()
    ) {
      value = bucketStr.slice(prefix.length).trim();
    } else {
      const idx = bucketStr.indexOf(":");
      value = idx >= 0 ? bucketStr.slice(idx + 1).trim() : bucketStr;
    }
    if (!value) return [];
    return [{ key: normalizeSituationClauseKey(metricKey), value }];
  }
  if (p.kind === "situation") {
    const raw = meta && Array.isArray(meta.clauses) ? meta.clauses : [];
    const out: Array<{ key: string; value: string }> = [];
    for (const c of raw) {
      const r = pickClauseRecordFromRaw(c);
      if (r) out.push(r);
    }
    if (out.length) return out;
    const situationStr =
      (meta && typeof meta.situation === "string" && meta.situation.includes("=") && meta.situation.trim()) ||
      (typeof p.label === "string" && p.label.includes("=") ? p.label.trim() : "");
    if (situationStr) return parsePipeEqClauses(situationStr);
  }
  return [];
}

function resolveGatheredClauses(
  clauses: Array<{ key: string; value: string }>,
  catalog: PickImportCatalogContext
): { nodes: TradePolicyPolicyNode[]; unresolved: string[] } {
  const unresolved: string[] = [];
  const children: TradePolicyRuleNode[] = [];
  for (const { key, value } of clauses) {
    if (!key?.trim() || !value?.trim()) continue;
    const resolved = resolveOneSituationClause(key, value, catalog);
    if (!resolved) unresolved.push(normalizeSituationClauseKey(key));
    else children.push(resolved);
  }
  if (unresolved.length) return { nodes: [], unresolved };
  if (children.length === 0) return { nodes: [], unresolved: ["clauses"] };
  const root: TradePolicyPolicyNode =
    children.length === 1 ? children[0] : { kind: "group", op: "and", children };
  return { nodes: [root], unresolved: [] };
}

function pickContentPolicyNodesWithCatalog(
  p: ReportPick,
  catalog: PickImportCatalogContext
): { nodes: TradePolicyPolicyNode[]; unresolved: string[] } {
  const { fieldsByPath } = catalog;
  // Prefer explicit backend suggestions.
  if (Array.isArray(p.policyNodes)) {
    const nodes = p.policyNodes.filter(isPolicyNode);
    if (nodes.length) return { nodes, unresolved: [] };
  }
  // Legacy direct rule.
  if (p.rule) {
    const path = p.rule.path;
    const op = p.rule.op;
    if (!path || !op) return { nodes: [], unresolved: ["rule"] };
    let value: string | number | string[] | null = null;
    if (p.rule.valueNumber != null && Number.isFinite(p.rule.valueNumber)) value = p.rule.valueNumber;
    else if (Array.isArray(p.rule.valueEnumSet) && p.rule.valueEnumSet.length) value = p.rule.valueEnumSet;
    else if (typeof p.rule.valueEnum === "string" && p.rule.valueEnum.trim()) value = p.rule.valueEnum.trim();
    if (value == null) return { nodes: [], unresolved: ["rule"] };
    return { nodes: [{ kind: "rule", path, op, value } as TradePolicyPolicyNode], unresolved: [] };
  }
  if (p.kind === "bucket" || p.kind === "situation") {
    const clauses = gatherClausesForPick(p);
    if (!clauses.length) {
      return { nodes: [], unresolved: [p.kind === "bucket" ? "bucket" : "clauses"] };
    }
    return resolveGatheredClauses(clauses, catalog);
  }
  return { nodes: [], unresolved: ["content"] };
}

/**
 * Full policy nodes for one pick: slice constraints AND‑combined with bucket/rule content when slice context exists.
 * Returns [] if the pick has slice context but the catalog cannot resolve every required slice dimension (strict).
 */
export function pickImportPolicyNodes(p: ReportPick, catalog: PickImportCatalogContext): TradePolicyPolicyNode[] {
  const { fieldsByPath } = catalog;
  if (
    p.kind === "segment" &&
    p.sliceContext &&
    p.sliceContext.kind !== "all" &&
    p.sliceContext.sliceId !== "all"
  ) {
    const { nodes, unresolved } = sliceContextToPolicyNodes(p.sliceContext, fieldsByPath);
    if (unresolved.length) return [];
    if (nodes.length === 0) return [];
    return nodes.length === 1 ? nodes : [{ kind: "group", op: "and", children: nodes }];
  }

  const contentResolved = pickContentPolicyNodesWithCatalog(p, catalog);
  const content = contentResolved.nodes;
  const ctx = p.sliceContext;
  if (!ctx || ctx.kind === "all" || ctx.sliceId === "all") {
    return content;
  }
  if (content.length === 0) return [];

  const { nodes: sliceNodes, unresolved } = sliceContextToPolicyNodes(ctx, fieldsByPath);
  if (unresolved.length > 0) return [];

  if (sliceNodes.length === 0) return content;

  return [{ kind: "group", op: "and", children: [...sliceNodes, ...content] }];
}

export function pickHasImportableContent(p: ReportPick): boolean {
  if (p.kind === "exit_placement") {
    return Boolean(p.meta?.simulation_rule);
  }
  if (p.kind === "segment" && p.sliceContext && p.sliceContext.kind !== "all") return true;
  return pickContentPolicyNodes(p).length > 0;
}

export type PickImportDiagnostics = {
  hasContent: boolean;
  hasSliceContext: boolean;
  importable: boolean;
  unresolvedSliceDimensions: string[];
  unresolvedContentKeys: string[];
};

export function pickImportDiagnostics(p: ReportPick, catalog: PickImportCatalogContext): PickImportDiagnostics {
  if (p.kind === "exit_placement") {
    const ready = Boolean(p.meta?.simulation_rule);
    return {
      hasContent: ready,
      hasSliceContext: Boolean(p.sliceId),
      importable: ready,
      unresolvedSliceDimensions: [],
      unresolvedContentKeys: [],
    };
  }
  const diag = pickImportDiagnosticsBase(p, catalog);
  return {
    ...diag,
    importable: pickImportPolicyNodes(p, catalog).length > 0,
  };
}

function pickImportDiagnosticsBase(p: ReportPick, catalog: PickImportCatalogContext): PickImportDiagnostics {
  const { fieldsByPath } = catalog;
  if (Array.isArray(p.policyNodes) && p.policyNodes.length > 0) {
    const hasSliceContext = Boolean(p.sliceContext && p.sliceContext.kind !== "all" && p.sliceContext.sliceId !== "all");
    if (hasSliceContext && p.sliceContext) {
      const { unresolved } = sliceContextToPolicyNodes(p.sliceContext, fieldsByPath);
      if (unresolved.length > 0) {
        return {
          hasContent: true,
          hasSliceContext,
          importable: false,
          unresolvedSliceDimensions: unresolved,
          unresolvedContentKeys: [],
        };
      }
    }
    const v = validatePolicyNodesAgainstCatalog(p.policyNodes, fieldsByPath);
    return {
      hasContent: true,
      hasSliceContext,
      importable: v.ok,
      unresolvedSliceDimensions: [],
      unresolvedContentKeys: v.ok ? [] : v.unresolved,
    };
  }
  if (p.kind === "segment" && p.sliceContext) {
    const { nodes, unresolved } = sliceContextToPolicyNodes(p.sliceContext, fieldsByPath);
    return {
      hasContent: true,
      hasSliceContext: true,
      importable: unresolved.length === 0 && nodes.length > 0,
      unresolvedSliceDimensions: unresolved,
      unresolvedContentKeys: [],
    };
  }

  const hasContent = pickHasImportableContent(p);
  const ctx = p.sliceContext;
  const hasSliceContext = Boolean(ctx && ctx.kind !== "all" && ctx.sliceId !== "all");
  if (!hasContent) {
    return { hasContent, hasSliceContext, importable: false, unresolvedSliceDimensions: [], unresolvedContentKeys: [] };
  }
  if (!hasSliceContext || !ctx) {
    const c = pickContentPolicyNodesWithCatalog(p, catalog);
    return {
      hasContent,
      hasSliceContext,
      importable: c.unresolved.length === 0,
      unresolvedSliceDimensions: [],
      unresolvedContentKeys: c.unresolved,
    };
  }
  const { unresolved } = sliceContextToPolicyNodes(ctx, fieldsByPath);
  if (unresolved.length > 0) {
    return {
      hasContent,
      hasSliceContext,
      importable: false,
      unresolvedSliceDimensions: unresolved,
      unresolvedContentKeys: [],
    };
  }
  const c = pickContentPolicyNodesWithCatalog(p, catalog);
  return {
    hasContent,
    hasSliceContext,
    importable: c.unresolved.length === 0,
    unresolvedSliceDimensions: [],
    unresolvedContentKeys: c.unresolved,
  };
}

/**
 * Map a `regime_summary` flat-table row to slice context for simulator import (`kind: "segment"` picks).
 * `tableKey` is the BE field name (e.g. candle_direction_and_trade_direction).
 */
export function segmentSliceContextFromSummaryTable(
  tableKey: string,
  rowKey: string
): ReportPickSliceContext | null {
  const sid = (parts: string[]) => `summary_${parts.map((p) => summarySliceIdPart(p)).join("_")}`;

  switch (tableKey) {
    case "market_condition":
      return {
        sliceId: sid([tableKey, rowKey]),
        kind: "regime",
        label: `Regime: ${rowKey}`,
        market_condition: rowKey,
      };
    case "market_condition_and_direction": {
      const p = parseRegimeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid([tableKey, p.market_condition, p.candle_direction]),
        kind: "regime_direction",
        label: `${p.market_condition} | ${p.candle_direction}`,
        market_condition: p.market_condition,
        candle_direction: p.candle_direction,
      };
    }
    case "candle_direction":
      return {
        sliceId: sid([tableKey, rowKey]),
        kind: "candle_direction",
        label: `Market candle direction (${DIRECTION_LONG_BARS_LABEL}): ${rowKey}`,
        candle_direction: rowKey,
      };
    case "candle_direction_short":
    case "market_direction_short":
      return {
        sliceId: sid([tableKey, rowKey]),
        kind: "market_direction_short",
        label: `Market candle direction (${DIRECTION_SHORT_BARS_LABEL}): ${rowKey}`,
        candle_direction: rowKey,
      };
    case "trade_direction": {
      if (rowKey !== "long" && rowKey !== "short") return null;
      return {
        sliceId: sid([tableKey, rowKey]),
        kind: "direction",
        label: rowKey === "long" ? "Long" : "Short",
        trade_direction: rowKey,
      };
    }
    case "session": {
      const tok = rowKey.trim();
      if (!tok) return null;
      return {
        sliceId: sid([tableKey, tok]),
        kind: "session_time",
        label: `Session: ${tok.replace(/_/g, " ")}`,
        session_id: tok,
      };
    }
    case "candle_direction_and_trade_direction": {
      const p = parseCandleDirectionTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid([tableKey, p.candle_direction, p.trade_direction]),
        kind: "candle_direction_trade_direction",
        label: `${p.candle_direction} | ${p.trade_direction} · Market candle direction (${DIRECTION_LONG_BARS_LABEL}) + trade direction`,
        candle_direction: p.candle_direction,
        trade_direction: p.trade_direction,
      };
    }
    case "candle_direction_short_and_trade_direction": {
      const p = parseCandleDirectionTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["cds_td", p.candle_direction, p.trade_direction]),
        kind: "candle_direction_short_trade_direction",
        label: `${p.candle_direction} | ${p.trade_direction} · Market candle direction (${DIRECTION_SHORT_BARS_LABEL}) + trade direction`,
        candle_direction: p.candle_direction,
        trade_direction: p.trade_direction,
      };
    }
    case "market_condition_candle_direction_and_trade_direction": {
      const p = parseRegimeDirectionTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["triple", p.market_condition, p.candle_direction, p.trade_direction]),
        kind: "regime_direction_trade_direction",
        label: `${p.market_condition} | ${p.candle_direction} | ${p.trade_direction} (${DIRECTION_LONG_BARS_LABEL})`,
        market_condition: p.market_condition,
        candle_direction: p.candle_direction,
        trade_direction: p.trade_direction,
        triple_candle_window: "long",
      };
    }
    case "market_condition_candle_direction_short_and_trade_direction": {
      const p = parseRegimeDirectionTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["triple_short", p.market_condition, p.candle_direction, p.trade_direction]),
        kind: "regime_direction_trade_direction",
        label: `${p.market_condition} | ${p.candle_direction} | ${p.trade_direction} (${DIRECTION_SHORT_BARS_LABEL})`,
        market_condition: p.market_condition,
        candle_direction: p.candle_direction,
        trade_direction: p.trade_direction,
        triple_candle_window: "short",
      };
    }
    case "session_and_trade_direction": {
      const p = parseSessionTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["sess_td", p.session_id, p.trade_direction]),
        kind: "session_trade_direction",
        label: `${p.session_id.replace(/_/g, " ")} | ${p.trade_direction}`,
        session_id: p.session_id,
        trade_direction: p.trade_direction,
      };
    }
    case "session_and_candle_direction": {
      const p = parseSessionCandleDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["sess_cd", p.session_id, p.candle_direction]),
        kind: "session_candle_direction",
        label: `${p.session_id.replace(/_/g, " ")} | ${p.candle_direction} · Market candle direction (${DIRECTION_LONG_BARS_LABEL})`,
        session_id: p.session_id,
        candle_direction: p.candle_direction,
      };
    }
    case "session_and_candle_direction_short": {
      const p = parseSessionCandleDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["sess_cds", p.session_id, p.candle_direction]),
        kind: "session_candle_direction_short",
        label: `${p.session_id.replace(/_/g, " ")} | ${p.candle_direction} · Market candle direction (${DIRECTION_SHORT_BARS_LABEL})`,
        session_id: p.session_id,
        candle_direction: p.candle_direction,
      };
    }
    case "session_and_market_condition": {
      const p = parseSessionMarketConditionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["sess_mc", p.session_id, p.market_condition]),
        kind: "session_regime",
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition}`,
        session_id: p.session_id,
        market_condition: p.market_condition,
      };
    }
    case "session_and_market_condition_and_trade_direction": {
      const p = parseSessionMarketConditionTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["sess_mc_td", p.session_id, p.market_condition, p.trade_direction]),
        kind: "session_regime_trade_direction",
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition} | ${p.trade_direction}`,
        session_id: p.session_id,
        market_condition: p.market_condition,
        trade_direction: p.trade_direction,
      };
    }
    case "session_and_market_condition_and_direction_and_trade_direction": {
      const p = parseSessionMarketConditionCandleTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["sess_mcd_td", p.session_id, p.market_condition, p.candle_direction, p.trade_direction]),
        kind: "session_regime_direction_trade_direction",
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition} | ${p.candle_direction} | ${p.trade_direction} (${DIRECTION_LONG_BARS_LABEL})`,
        session_id: p.session_id,
        market_condition: p.market_condition,
        candle_direction: p.candle_direction,
        trade_direction: p.trade_direction,
        triple_candle_window: "long",
      };
    }
    case "session_and_market_condition_and_direction_short_and_trade_direction": {
      const p = parseSessionMarketConditionCandleTradeDirectionKey(rowKey);
      if (!p) return null;
      return {
        sliceId: sid(["sess_mcdstd", p.session_id, p.market_condition, p.candle_direction, p.trade_direction]),
        kind: "session_regime_direction_short_trade_direction",
        label: `${p.session_id.replace(/_/g, " ")} | ${p.market_condition} | ${p.candle_direction} | ${p.trade_direction} (${DIRECTION_SHORT_BARS_LABEL})`,
        session_id: p.session_id,
        market_condition: p.market_condition,
        candle_direction: p.candle_direction,
        trade_direction: p.trade_direction,
        triple_candle_window: "short",
      };
    }
    default:
      return null;
  }
}
